/*
 * Native Possum — np-bag-drawer.js
 * Behaviour for sections/np-bag-drawer.liquid (see its header). Loaded deferred;
 * window.bagDrawer exists from DOMContentLoaded (np-header.js uses it on click).
 */
// ============================================
// BAG DRAWER v42
// Markup is Liquid (this section); np-cart.js re-renders #bagEmpty,
// #bagItems, #bagFooter and #bagFooterMobile after every cart change.
// This script: open/close (npLayer), the free-shipping bar, header badges,
// and one delegated listener that sends bag controls to npCart.
// ============================================

// ============================================
// TOAST (visual only — npCart.announce speaks for screen readers)
// ============================================

// isError: stays until the shopper acts; otherwise a reading time (npMessage)
function bagShowToast(message, isError) {
  var toast = document.getElementById('npBagToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'npBagToast';
    toast.className = 'np-bag-toast';
    toast.setAttribute('aria-hidden', 'true');
    document.getElementById('bagDrawer').appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.npMessage[isError ? 'hold' : 'dwell'](toast, function () {
    toast.classList.remove('is-visible');
  });
}

// ============================================
// BAG DRAWER CLASS
// ============================================

class BagDrawer {
  constructor() {
    this.drawer = document.getElementById('bagDrawer');
    this.backdrop = document.getElementById('bagBackdrop');
    this.closeBtn = document.getElementById('bagClose');

    // v40: Progress bar elements (not re-rendered — JS-drawn)
    this.bagProgress = document.getElementById('bagProgress');
    this.bagProgressText = document.getElementById('bagProgressText');
    this.bagProgressFill = document.getElementById('bagProgressFill');

    // The header stays usable while the bag is open (option C: the header
    // bag button is the close on phone). It opens/closes the bag itself.
    this.keepEls = [document.getElementById('npHeader')].filter(Boolean);

    this.init();
  }

  init() {
    this.closeBtn.addEventListener('click', () => this.close());

    // Everything inside the re-rendered parts is replaced on each cart
    // change, so controls are handled by delegation on the drawer.
    this.drawer.addEventListener('click', (e) => this.onClick(e));
    this.drawer.addEventListener('change', (e) => this.onChange(e));

    this.initSwipeToClose();
    this.initAddToBagListeners();

    document.addEventListener('np:cart:updated', (e) => this.onCartUpdated(e.detail || {}));
    this.renderProgress();

    // v47: Reopen bag drawer after currency switch reload
    try {
      if (sessionStorage.getItem('np-reopen-bag') === '1') {
        sessionStorage.removeItem('np-reopen-bag');
        this.open();
      }
    } catch (e) {}
  }

  isOpen() {
    return this.drawer.classList.contains('active');
  }

  // Scroll lock, focus, inert page, Escape and "only one of search / menu /
  // bag" are npLayer's (group 'header'). Wider screens keep the page's
  // scrollbar hidden until the drawer has slid out (300ms).
  open(trigger) {
    if (this.isOpen()) return;
    var active = document.activeElement;
    trigger = trigger || (active && active !== document.body ? active : null);

    this.drawer.classList.add('active');
    this.backdrop.classList.add('active');

    window.npLayer.open(this.drawer, {
      trigger: trigger,
      keep: this.keepEls,
      backdrop: this.backdrop,
      closeOnKeepUse: true,
      group: 'header',
      lockScroll: window.innerWidth <= 767 ? true : { unlockDelay: 300 },
      // Phone: first bag item (header hidden); else the empty-state link via
      // npLayer's first-visible fallback. Desktop: the close button.
      initialFocus: window.innerWidth <= 767 ? '#bagItems button, #bagItems a' : '#bagClose',
      onRequestClose: () => this.close()
    });
  }

  close() {
    if (!this.isOpen()) return;
    this.drawer.classList.remove('active');
    this.backdrop.classList.remove('active');
    window.npLayer.close(this.drawer);
  }

  // Re-sync with the server cart (another tab may have changed it).
  // Called by np-header.js when the bag button opens the drawer.
  updateBag() {
    if (!window.npCart) return Promise.resolve();
    return window.npCart.refresh({ source: 'open' }).catch(function () {});
  }

  onCartUpdated(detail) {
    var count = detail.cart ? detail.cart.item_count : null;
    if (count === null) {
      var items = document.getElementById('bagItems');
      count = items ? parseInt(items.getAttribute('data-cart-count'), 10) || 0 : 0;
    }
    this.updateBagCount(count);
    this.renderProgress();
    if (detail.openBag) this.open(detail.trigger);
  }

  // The header's bag badge. Its button label follows the badge (np-header.js).
  updateBagCount(count) {
    document.querySelectorAll('[data-np-bag-count]').forEach(function (badge) {
      badge.textContent = count;
      badge.classList.toggle('hidden', !(count > 0));
    });
  }

  // v40: Shipping progress bar — reads the cart from #bagItems' data-cart-*
  // (Liquid) and the threshold from window.NP_SHIPPING (theme settings).
  renderProgress() {
    var items = document.getElementById('bagItems');
    if (!items || !this.bagProgress) return;
    var count = parseInt(items.getAttribute('data-cart-count'), 10) || 0;
    var total = parseInt(items.getAttribute('data-cart-total'), 10) || 0;
    var currency = items.getAttribute('data-cart-currency');
    var physical = items.getAttribute('data-requires-shipping') === 'true';
    var thresholds = (window.NP_SHIPPING && window.NP_SHIPPING.thresholdsNZD) || {};
    var thresholdNZDCents = thresholds[currency] || 0;

    // Empty bag, gift cards only, or no threshold for this currency — hide bar
    if (!count || !physical || !thresholdNZDCents) {
      this.bagProgress.classList.remove('show');
      return;
    }

    // Convert NZD threshold to presentment currency using Shopify's live rate
    var rate = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate)
      : 1;
    var thresholdConverted = thresholdNZDCents * rate;

    var remaining = thresholdConverted - total;
    var percentage = Math.min(100, Math.round((total / thresholdConverted) * 100));

    if (remaining > 0) {
      this.bagProgress.classList.remove('bag-progress--success');
      this.bagProgressText.innerHTML = 'You’re <strong>' + window.npEscapeHtml(window.npFormatMoney(window.npCeilToFive(remaining))) + '</strong> away from free shipping';
      this.bagProgressFill.style.width = percentage + '%';
    } else {
      this.bagProgress.classList.add('bag-progress--success');
      this.bagProgressText.innerHTML = '<span aria-hidden="true">✓</span> You’ve unlocked <strong>free shipping!</strong>';
      this.bagProgressFill.style.width = '100%';
    }

    this.bagProgress.classList.add('show');
  }

  onClick(e) {
    var t = e.target && e.target.closest ? e.target.closest('button, a') : null;
    if (!t || !this.drawer.contains(t)) return;

    if (t.classList.contains('qty-plus') || t.classList.contains('qty-minus')) {
      this.onQuantity(t);
    } else if (t.id === 'btnCheckout' || t.id === 'btnCheckoutMobile') {
      this.checkout();
    } else if (t.id === 'btnViewBag') {
      // Normal link navigation to /cart — just close the bag first
      e.stopPropagation();
      this.close();
    }
  }

  onChange(e) {
    // v44/v46: Desktop currency select submits the localization form —
    // form.submit() (in npSwitchCurrency) is the only thing that changes market.
    if (e.target && e.target.id === 'bagCurrencySelect') {
      // v47: Flag bag as open so it reopens after reload
      try { sessionStorage.setItem('np-reopen-bag', '1'); } catch (err) {}
      window.npSwitchCurrency(e.target.value, document.getElementById('bagCurrencyForm'));
    }
  }

  // +/− by line KEY with the quantity Liquid rendered for that line. All of
  // the line's controls are busy until the request (queued in npCart) is
  // done, so a second click can never send a stale quantity.
  onQuantity(btn) {
    var key = btn.getAttribute('data-key');
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 0;
    var itemEl = btn.closest('.bag-item');
    var title = itemEl ? itemEl.getAttribute('data-title') : '';
    var controls = itemEl ? itemEl.querySelectorAll('.qty-btn') : [btn];
    var fail = function (err) { bagShowToast(err.message, true); };

    if (btn.classList.contains('qty-plus')) {
      window.npCart.change(key, qty + 1, { control: controls, title: title }).catch(fail);
    } else if (qty <= 1) {
      // v35: Direct remove — no confirmation overlay
      this.removeItem(key, itemEl, controls, title);
    } else {
      window.npCart.change(key, qty - 1, { control: controls, title: title }).catch(fail);
    }
  }

  // v35: Fade+scale (500ms, matching wishlist) runs while the request is in
  // flight; the re-render waits for both.
  removeItem(key, itemEl, controls, title) {
    var fade = null;
    if (itemEl && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      itemEl.classList.add('is-removing');
      fade = new Promise(function (resolve) { setTimeout(resolve, 500); });
    }
    window.npCart.remove(key, { control: controls, title: title, waitFor: fade })
      .then(function () {
        bagShowToast((title || 'Item') + ' removed from bag');
      })
      .catch(function (err) {
        if (itemEl) itemEl.classList.remove('is-removing');
        bagShowToast(err.message, true);
      });
  }

  // v42: EU orders paid on delivery (DAP) need the tick box first
  // (np-eu-delivery.js shows the error and focuses the box).
  checkout() {
    if (window.npEuDelivery && !window.npEuDelivery.allowCheckout(this.drawer)) return;
    window.location.href = '/checkout';
  }

  // Any <form> with an [name="add"] control adds via npCart. The bag opens
  // on success (np:cart:updated → onCartUpdated); failures show beside the
  // button and are announced by npCart.
  initAddToBagListeners() {
    document.addEventListener('submit', (e) => {
      var form = e.target;
      if (!form || !form.querySelector || !form.querySelector('[name="add"]')) return;
      e.preventDefault();
      var submitter = e.submitter || form.querySelector('[name="add"]');
      form.querySelectorAll('[data-np-add-error]').forEach(function (el) { el.remove(); });
      var formData = new FormData(form);

      window.npCart.add(formData, { control: submitter }).catch(function (err) {
        var msg = document.createElement('div');
        msg.className = 'error-message';
        msg.setAttribute('data-np-add-error', '');
        msg.textContent = err.message;
        submitter.insertAdjacentElement('beforebegin', msg);
        window.npMessage.hold(msg, function () { msg.remove(); });
      });
    });
  }

  initSwipeToClose() {
    var touchStartX = 0;
    var touchEndX = 0;

    this.drawer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.drawer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      if (touchEndX > touchStartX + 50) {
        this.close();
      }
    }, { passive: true });
  }
}

// ============================================
// INITIALIZE BAG DRAWER
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  window.bagDrawer = new BagDrawer();
});
